import axios from 'axios';

// Use different base URLs for development vs production
const API_BASE_URL = import.meta.env.DEV 
  ? '/api'  // Use Vite proxy in development
  : (import.meta.env.VITE_API_URL || 'http://localhost:5000/api'); // Direct URL in production
  
console.log('API Base URL:', API_BASE_URL, 'Development mode:', import.meta.env.DEV);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('No token found in localStorage for request:', config.url);
    }
    
    // If sending FormData, remove the default Content-Type header
    // to let the browser set the multipart boundary automatically
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    console.log(`API Request: ${config.method.toUpperCase()} ${config.url}`, {
      headers: config.headers,
      params: config.params,
      data: config.data,
      hasToken: !!token
    });
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.config.method.toUpperCase()} ${response.config.url}`, {
      status: response.status,
      data: response.data
    });
    return response;
  },
  (error) => {
    console.error('API Response Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      headers: error.config?.headers
    });
    
    if (error.response?.status === 401) {
      console.error('Authentication error detected. Token in localStorage:', localStorage.getItem('token'));
      console.error('Full error details:', error);
      
      // Save error for debugging
      sessionStorage.setItem('lastAuthError', JSON.stringify({
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
        timestamp: new Date().toISOString()
      }));
      
      // Don't redirect here - let the component handle it
      // This prevents redirect loops
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getCurrentUser: () => {
    console.log('Calling getCurrentUser API');
    return api.get('/auth/me')
      .then(response => {
        console.log('getCurrentUser API response:', response.data);
        return response;
      })
      .catch(error => {
        console.error('getCurrentUser API error:', error);
        throw error;
      });
  },
  changePassword: (data) => api.post('/auth/change-password', data),
  sendOTP: (data) => api.post('/auth/send-otp', data),
  verifyOTP: (data) => api.post('/auth/verify-otp', data),
  
  // Employee verification and setup
  verifyEmployeeToken: (token) => api.get(`/employees/verify/${token}`),
  setEmployeePassword: (token, data) => api.post(`/employees/verify/${token}`, data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  
  // Token validation
  validateToken: () => api.get('/auth/validate-token'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentOrders: () => api.get('/dashboard/recent-orders'),
  getLowStockProducts: () => api.get('/dashboard/low-stock'),
};

// Products API
export const productsAPI = {
  getAll: (params) => {
    console.log('Calling products API with params:', params);
    return api.get('/products', { params })
      .then(response => {
        console.log('Products API raw response:', response);
        return response;
      })
      .catch(error => {
        console.error('Products API error:', error);
        throw error;
      });
  },
  getById: (uuid) => api.get(`/products/${uuid}`),
  create: (data) => {
    console.log('Creating product with data:', data);
    // For FormData, don't set Content-Type header - let the browser set it automatically with boundary
    if (data instanceof FormData) {
      return api.post('/products', data)
        .then(response => {
          console.log('Create product response:', response);
          return response;
        })
        .catch(error => {
          console.error('Create product error:', error);
          throw error;
        });
    }
    return api.post('/products', data)
      .then(response => {
        console.log('Create product response:', response);
        return response;
      })
      .catch(error => {
        console.error('Create product error:', error);
        throw error;
      });
  },
  update: (uuid, data) => {
    console.log('Updating product with UUID:', uuid, 'and data:', data);
    // For FormData, don't set Content-Type header - let the browser set it automatically with boundary
    if (data instanceof FormData) {
      return api.put(`/products/${uuid}`, data)
        .then(response => {
          console.log('Update product response:', response);
          return response;
        })
        .catch(error => {
          console.error('Update product error:', error);
          throw error;
        });
    }
    return api.put(`/products/${uuid}`, data)
      .then(response => {
        console.log('Update product response:', response);
        return response;
      })
      .catch(error => {
        console.error('Update product error:', error);
        throw error;
      });
  },
  delete: (uuid) => api.delete(`/products/${uuid}`),
  toggleStatus: (uuid) => api.patch(`/products/${uuid}/toggle-status`),
  getStats: () => api.get('/products/stats/overview'),
};

// Categories API
export const categoriesAPI = {
  getAll: (params) => {
    console.log('Calling categories API with params:', params);
    return api.get('/categories', { params })
      .then(response => {
        console.log('Categories API raw response:', response);
        // Ensure we're returning the response in the expected format
        if (response.data && !response.data.success) {
          // If the API doesn't return in the success/data format, wrap it
          return {
            data: {
              success: true,
              data: response.data,
              pagination: response.data.pagination || { page: 1, limit: 10, total: response.data.length, pages: 1 }
            }
          };
        }
        return response;
      })
      .catch(error => {
        console.error('Categories API error:', error);
        throw error;
      });
  },
  getById: (uuid) => api.get(`/categories/${uuid}`),
  create: (data) => {
    // For FormData, don't set Content-Type header - let the browser set it automatically with boundary
    if (data instanceof FormData) {
      return api.post('/categories', data);
    }
    return api.post('/categories', data);
  },
  update: (uuid, data) => {
    // For FormData, don't set Content-Type header - let the browser set it automatically with boundary
    if (data instanceof FormData) {
      return api.put(`/categories/${uuid}`, data);
    }
    return api.put(`/categories/${uuid}`, data);
  },
  delete: (uuid) => api.delete(`/categories/${uuid}`),
  getSubcategories: (categoryUuid) => {
    console.log('Fetching subcategories for category:', categoryUuid);
    return api.get(`/categories/${categoryUuid}/subcategories`)
      .then(response => {
        console.log('Subcategories API raw response:', response);
        // Ensure we're returning the response in the expected format
        if (response.data && !response.data.success) {
          // If the API doesn't return in the success/data format, wrap it
          return {
            data: {
              success: true,
              data: response.data,
              pagination: response.data.pagination || { page: 1, limit: 10, total: response.data.length, pages: 1 }
            }
          };
        }
        return response;
      })
      .catch(error => {
        console.error('Subcategories API error:', error);
        throw error;
      });
  },
  // NOTE: Removed getProductGroups method since product groups now link directly to categories
  // Use productGroupsAPI.getAll({ category_uuid: categoryUuid }) instead
  toggleStatus: (uuid) => api.patch(`/categories/${uuid}/toggle-status`),
};

// Note: Subcategories have been replaced with Companies
// Use companiesAPI instead (imported from companiesAPI.js)

// Orders API
export const ordersAPI = {
  getAll: (params) => {
    console.log('Calling orders API with params:', params);
    return api.get('/orders', { params })
      .then(response => {
        console.log('Orders API raw response:', response);
        // Ensure we're returning the response in the expected format
        if (response.data && !response.data.success) {
          // If the API doesn't return in the success/data format, wrap it
          return {
            data: {
              success: true,
              data: response.data,
              pagination: response.data.pagination || { page: 1, limit: 10, total: response.data.length, pages: 1 }
            }
          };
        }
        return response;
      })
      .catch(error => {
        console.error('Orders API error:', error);
        throw error;
      });
  },
  getById: (uuid) => api.get(`/orders/${uuid}`),
  create: (data) => api.post('/orders', data),
  update: (uuid, data) => api.put(`/orders/${uuid}`, data),
  updateStatus: (uuid, status) => api.patch(`/orders/${uuid}/status`, { status }),
  delete: (uuid) => api.delete(`/orders/${uuid}`),
  getStats: () => api.get('/orders/stats/overview'),
};

// Customers API
export const customersAPI = {
  getAll: (params) => {
    console.log('Calling customers API with params:', params);
    return api.get('/customers', { params })
      .then(response => {
        console.log('Customers API raw response:', response);
        // Ensure we're returning the response in the expected format
        if (response.data && !response.data.success) {
          // If the API doesn't return in the success/data format, wrap it
          return {
            data: {
              success: true,
              data: response.data,
              pagination: response.data.pagination || { page: 1, limit: 10, total: response.data.length, pages: 1 }
            }
          };
        }
        return response;
      })
      .catch(error => {
        console.error('Customers API error:', error);
        throw error;
      });
  },
  getWithSubscriptions: (params) => {
    console.log('Calling customers with subscriptions API with params:', params);
    return api.get('/customers/subscriptions/list', { params })
      .then(response => {
        console.log('Customers with subscriptions API raw response:', response);
        if (response.data && !response.data.success) {
          return {
            data: {
              success: true,
              data: response.data,
              pagination: response.data.pagination || { page: 1, limit: 10, total: response.data.length, pages: 1 }
            }
          };
        }
        return response;
      })
      .catch(error => {
        console.error('Customers with subscriptions API error:', error);
        throw error;
      });
  },
  getById: (uuid) => api.get(`/customers/${uuid}`),
  create: (data) => api.post('/customers', data),
  update: (uuid, data) => api.put(`/customers/${uuid}`, data),
  delete: (uuid) => api.delete(`/customers/${uuid}`),
  getStats: () => api.get('/customers/stats/overview'),
};

// Employees API
export const employeesAPI = {
  getAll: (params = {}) => {
    // Always filter for active employees unless explicitly specified
    const requestParams = {
      is_active: 'true',
      ...params
    };
    return api.get('/employees', { params: requestParams });
  },
  getAllIncludingInactive: (params) => api.get('/employees', { params: { is_active: undefined, ...params } }),
  getById: (uuid) => api.get(`/employees/${uuid}`),
  create: (data) => api.post('/employees', data),
  update: (uuid, data) => {
    console.log('employeesAPI.update called with:', { uuid, data });
    
    // Verify token exists before making the request
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No authentication token found in localStorage');
      throw new Error('No authentication token found');
    }
    
    console.log('Token verification passed, making PUT request to:', `/employees/${uuid}`);
    console.log('Request data:', data);
    
    return api.put(`/employees/${uuid}`, data)
      .then(response => {
        console.log('Employee update API response:', response);
        return response;
      })
      .catch(error => {
        console.error('Employee update API error:', error);
        console.error('Error details:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.response?.headers,
          requestHeaders: error.config?.headers,
          config: error.config
        });
        
        // Let's check if authorization header is present
        console.error('Request headers sent:', error.config?.headers);
        console.error('Authorization header:', error.config?.headers?.Authorization);
        
        throw error;
      });
  },
  delete: (uuid) => api.delete(`/employees/${uuid}`),
  resetPassword: (uuid, newPassword) => api.post(`/employees/${uuid}/reset-password`, { newPassword }),
  updatePermissions: (uuid, permissions) => api.patch(`/employees/${uuid}/permissions`, { permissions }),
  verify: (uuid) => api.patch(`/employees/${uuid}/verify`),
  getStats: () => api.get('/employees/stats/overview'),
  
  // Manual email sending
  sendVerificationEmail: (uuid) => {
    console.log('Sending verification email for employee UUID:', uuid);
    return api.post(`/employees/${uuid}/send-verification-email`)
      .then(response => {
        console.log('Send verification email response:', response);
        return response;
      })
      .catch(error => {
        console.error('Send verification email error:', error);
        throw error;
      });
  },
  
  sendPasswordResetEmail: (uuid) => {
    console.log('Sending password reset email for employee UUID:', uuid);
    return api.post(`/employees/${uuid}/send-password-reset-email`)
      .then(response => {
        console.log('Send password reset email response:', response);
        return response;
      })
      .catch(error => {
        console.error('Send password reset email error:', error);
        throw error;
      });
  },
};

// Subscriptions API
export const subscriptionsAPI = {
  getAll: (params) => {
    console.log('Calling subscriptions API with params:', params);
    return api.get('/subscriptions', { params })
      .then(response => {
        console.log('Subscriptions API raw response:', response);
        // Ensure we're returning the response in the expected format
        if (response.data && !response.data.success) {
          // If the API doesn't return in the success/data format, wrap it
          return {
            data: {
              success: true,
              data: response.data,
              pagination: response.data.pagination || { page: 1, limit: 10, total: response.data.length, pages: 1 }
            }
          };
        }
        return response;
      })
      .catch(error => {
        console.error('Subscriptions API error:', error);
        throw error;
      });
  },
  getById: (uuid) => api.get(`/subscriptions/${uuid}`),
  create: (data) => api.post('/subscriptions', data),
  update: (uuid, data) => api.put(`/subscriptions/${uuid}`, data),
  delete: (uuid) => {
    console.log('Subscriptions API: Deleting subscription with UUID:', uuid);
    return api.delete(`/subscriptions/${uuid}`)
      .then(response => {
        console.log('Subscriptions API: Delete response:', response);
        return response;
      })
      .catch(error => {
        console.error('Subscriptions API: Delete error:', error);
        throw error;
      });
  },
  getStats: () => api.get('/subscriptions/stats/overview'),
  getCompanyProducts: (companyUuid) => {
    console.log('Calling subscription company products API for company:', companyUuid);
    return api.get(`/subscriptions/companies/${companyUuid}/products`)
      .then(response => {
        console.log('Company products API response:', response);
        return response;
      })
      .catch(error => {
        console.error('Company products API error:', error);
        throw error;
      });
  },
};

// New comprehensive subscription APIs
export const subscriptionsCompleteAPI = {
  // Company Plans (what companies offer)
  getCompanyPlans: (params = {}) => api.get('/subscriptions-complete/company-plans', { params }),
  getCompanyPlanById: (uuid) => api.get(`/subscriptions-complete/company-plans/${uuid}`),
  
  // Customer Subscriptions (who subscribed to what)
  getCustomerSubscriptions: (params = {}) => api.get('/subscriptions-complete/customer-subscriptions', { params }),
  getCustomerSubscriptionById: (uuid) => api.get(`/subscriptions-complete/customer-subscriptions/${uuid}`),
  
  // Analytics
  getAnalytics: () => api.get('/subscriptions-complete/analytics'),
  
  // Actions
  createCustomerSubscription: (data) => api.post('/subscriptions-complete/customer-subscriptions', data),
  updateCustomerSubscription: (uuid, data) => api.put(`/subscriptions-complete/customer-subscriptions/${uuid}`, data),
  pauseSubscription: (uuid) => api.put(`/subscriptions-complete/customer-subscriptions/${uuid}/pause`),
  resumeSubscription: (uuid) => api.put(`/subscriptions-complete/customer-subscriptions/${uuid}/resume`),
  cancelSubscription: (uuid) => api.put(`/subscriptions-complete/customer-subscriptions/${uuid}/cancel`)
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => {
    console.log('Calling notifications API with params:', params);
    return api.get('/notifications', { params })
      .then(response => {
        console.log('Notifications API raw response:', response);
        
        // Ensure we're returning the response in the expected format
        if (response.data && !response.data.success) {
          // If the API doesn't return in the success/data format, wrap it
          return {
            data: {
              success: true,
              data: Array.isArray(response.data) ? response.data : [response.data],
              pagination: response.data.pagination || { page: 1, limit: 10, total: Array.isArray(response.data) ? response.data.length : 1, pages: 1 }
            }
          };
        }
        
        // If we have a success response but data is not an array, convert it
        if (response.data && response.data.success && response.data.data && !Array.isArray(response.data.data)) {
          console.log('Converting notifications data to array');
          return {
            ...response,
            data: {
              ...response.data,
              data: [response.data.data]
            }
          };
        }
        
        return response;
      })
      .catch(error => {
        console.error('Notifications API error:', error);
        throw error;
      });
  },
  getById: (uuid) => api.get(`/notifications/${uuid}`),
  delete: (uuid) => api.delete(`/notifications/${uuid}`),
  create: (data) => api.post('/notifications', data),
  update: (uuid, data) => api.put(`/notifications/${uuid}`, data),
  getStats: () => api.get('/notifications/stats/overview'),
};

// Settings API
export const settingsAPI = {
  getAll: () => {
    console.log('Calling settings API');
    return api.get('/settings')
      .then(response => {
        console.log('Settings API raw response:', response);
        
        // Ensure we're returning the response in the expected format
        if (response.data && !response.data.success) {
          // If the API doesn't return in the success/data format, wrap it
          return {
            data: {
              success: true,
              data: response.data
            }
          };
        }
        
        // If the data is an object but not an array, convert it to an array
        if (response.data && response.data.success && response.data.data && 
            !Array.isArray(response.data.data) && typeof response.data.data === 'object') {
          console.log('Converting settings object to array');
          const settingsArray = Object.keys(response.data.data).map(key => ({
            key: key,
            value: response.data.data[key].value,
            description: response.data.data[key].description
          }));
          
          return {
            ...response,
            data: {
              ...response.data,
              data: settingsArray
            }
          };
        }
        
        return response;
      })
      .catch(error => {
        console.error('Settings API error:', error);
        throw error;
      });
  },
  getByKey: (key) => api.get(`/settings/${key}`),
  update: (key, value) => api.put(`/settings/${key}`, { value }),
  create: (data) => api.post('/settings', data),
  delete: (key) => api.delete(`/settings/${key}`),
};

// Permissions API
export const permissionsAPI = {
  getAll: (params) => {
    console.log('Calling permissions API with params:', params);
    return api.get('/permissions', { params })
      .then(response => {
        console.log('Permissions API raw response:', response);
        // Ensure we're returning the response in the expected format
        if (response.data && !response.data.success) {
          // If the API doesn't return in the success/data format, wrap it
          return {
            data: {
              success: true,
              data: response.data,
              pagination: response.data.pagination || { page: 1, limit: 10, total: response.data.length, pages: 1 }
            }
          };
        }
        return response;
      })
      .catch(error => {
        console.error('Permissions API error:', error);
        throw error;
      });
  },
  getById: (uuid) => api.get(`/permissions/${uuid}`),
  create: (data) => api.post('/permissions', data),
  update: (uuid, data) => api.put(`/permissions/${uuid}`, data),
  delete: (uuid) => api.delete(`/permissions/${uuid}`),
  getEmployeeAssignments: () => {
    console.log('Calling employee assignments API');
    return api.get('/permissions/employees/assignments')
      .then(response => {
        console.log('Employee assignments API raw response:', response);
        // Ensure we're returning the response in the expected format
        if (response.data && !response.data.success) {
          // If the API doesn't return in the success/data format, wrap it
          return {
            data: {
              success: true,
              data: response.data
            }
          };
        }
        return response;
      })
      .catch(error => {
        console.error('Employee assignments API error:', error);
        throw error;
      });
  },
  getAvailable: () => {
    console.log('Calling available permissions API');
    return api.get('/permissions/available')
      .then(response => {
        console.log('Available permissions API raw response:', response);
        // Ensure we're returning the response in the expected format
        if (response.data && !response.data.success) {
          // If the API doesn't return in the success/data format, wrap it
          return {
            data: {
              success: true,
              data: response.data
            }
          };
        }
        return response;
      })
      .catch(error => {
        console.error('Available permissions API error:', error);
        throw error;
      });
  },
  getAssignable: () => {
    console.log('Calling assignable permissions API');
    return api.get('/permissions/assignable')
      .then(response => {
        console.log('Assignable permissions API raw response:', response);
        // Ensure we're returning the response in the expected format
        if (response.data && !response.data.success) {
          // If the API doesn't return in the success/data format, wrap it
          return {
            data: {
              success: true,
              data: response.data
            }
          };
        }
        return response;
      })
      .catch(error => {
        console.error('Assignable permissions API error:', error);
        throw error;
      });
  },
  assignToEmployee: (employeeUuid, permissionUuid) => 
    api.post(`/permissions/employees/${employeeUuid}/assign`, { permission_uuid: permissionUuid }),
  removeFromEmployee: (employeeUuid, permissionUuid) => 
    api.delete(`/permissions/employees/${employeeUuid}/remove/${permissionUuid}`),
  getTemplates: () => api.get('/permissions/templates'),
  applyTemplate: (employeeUuid, role) => 
    api.post('/permissions/templates/apply', { employeeUuid, role }),
};

// Companies API
export const companiesAPI = {
  getAll: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.is_active !== undefined) {
      searchParams.append('is_active', params.is_active);
    }
    if (params.search) {
      searchParams.append('search', params.search);
    }
    if (params.page) {
      searchParams.append('page', params.page);
    }
    if (params.limit) {
      searchParams.append('limit', params.limit);
    }
    
    const url = searchParams.toString() ? `/companies?${searchParams}` : '/companies';
    return api.get(url);
  },
  getById: (uuid) => api.get(`/companies/${uuid}`),
  create: (data) => api.post('/companies', data),
  update: (uuid, data) => api.put(`/companies/${uuid}`, data),
  delete: (uuid, force = false) => api.delete(`/companies/${uuid}${force ? '?force=true' : ''}`),
  toggleStatus: (uuid) => api.patch(`/companies/${uuid}/toggle-status`),
};

// Product Groups API
export const productGroupsAPI = {
  getAll: (params) => {
    console.log('Calling product groups API with params:', params);
    return api.get('/product-groups', { params })
      .then(response => {
        console.log('Product groups API raw response:', response);
        // Ensure we're returning the response in the expected format
        if (response.data && !response.data.success) {
          // If the API doesn't return in the success/data format, wrap it
          return {
            data: {
              success: true,
              data: response.data,
              pagination: response.data.pagination || { page: 1, limit: 10, total: response.data.length, pages: 1 }
            }
          };
        }
        return response;
      })
      .catch(error => {
        console.error('Product groups API error:', error);
        throw error;
      });
  },
  getById: (uuid) => api.get(`/product-groups/${uuid}`),
  create: (data) => {
    // For FormData, don't set Content-Type header - let the browser set it automatically with boundary
    if (data instanceof FormData) {
      return api.post('/product-groups', data);
    }
    return api.post('/product-groups', data);
  },
  update: (uuid, data) => {
    // For FormData, don't set Content-Type header - let the browser set it automatically with boundary
    if (data instanceof FormData) {
      return api.put(`/product-groups/${uuid}`, data);
    }
    return api.put(`/product-groups/${uuid}`, data);
  },
  delete: (uuid, force = false) => {
    console.log('ProductGroupsAPI delete called with UUID:', uuid, force ? '(force delete)' : '(soft delete)');
    const url = `/product-groups/${uuid}${force ? '?force=true' : ''}`;
    console.log('DELETE request URL:', url);
    return api.delete(url)
      .then(response => {
        console.log('Delete API response:', response);
        return response;
      })
      .catch(error => {
        console.error('Delete API error:', error);
        throw error;
      });
  },
  toggleStatus: (uuid) => api.patch(`/product-groups/${uuid}/toggle-status`),
  getProducts: (uuid) => api.get(`/product-groups/${uuid}/products`),
};

// Dashboard Enhanced API
export const dashboardEnhancedAPI = {
  getStats: () => api.get('/dashboard-enhanced/stats'),
  getRevenueTrends: () => api.get('/dashboard-enhanced/revenue-trends'),
  getOrderBreakdown: (params = {}) => api.get('/dashboard-enhanced/order-breakdown', { params }),
  getQuickView: () => api.get('/dashboard-enhanced/quick-view'),
  getSubscriptionAnalytics: () => api.get('/dashboard-enhanced/subscription-analytics'),
};

// Today's Orders API
export const todaysOrdersAPI = {
  getToday: (params = {}) => api.get('/todays-orders/today', { params }),
  getTomorrow: (params = {}) => api.get('/todays-orders/tomorrow', { params }),
  getWeekly: (params = {}) => api.get('/todays-orders/weekly', { params }),
  updateStatus: (uuid, status) => api.put(`/todays-orders/${uuid}/status`, { status }),
  getAnalytics: (params = {}) => api.get('/todays-orders/analytics/items', { params }),
};

// Delivery Boy API
export const deliveryBoyAPI = {
  // Delivery boy endpoints (for delivery boys themselves)
  getAvailableOrders: () => api.get('/delivery-boy/available-orders'),
  getMyOrders: (params = {}) => api.get('/delivery-boy/my-orders', { params }),
  assignOrder: (uuid, deliveryBoyUuid = null) => {
    const data = deliveryBoyUuid ? { deliveryBoyUuid } : {};
    return api.post(`/delivery-boy/orders/${uuid}/assign`, data);
  },
  updateOrderStatus: (uuid, status) => api.put(`/delivery-boy/orders/${uuid}/status`, { status }),
  completeDelivery: (uuid, notes = '', proofImageUrl = '') => 
    api.post(`/delivery-boy/orders/${uuid}/complete`, { notes, proofImageUrl }),
  getStats: () => api.get('/delivery-boy/stats'),
  
  // Bottle tracking endpoints
  updateBottles: (uuid, bottleData) => 
    api.post(`/delivery-boy/orders/${uuid}/bottles`, bottleData),
  getBottleHistory: (uuid) => api.get(`/delivery-boy/orders/${uuid}/bottles/history`),
  getCustomerBottleBalance: (customerUuid) => 
    api.get(`/delivery-boy/customers/${customerUuid}/bottles/balance`),
  
  // Admin endpoints (for managing all delivery boys)
  getAllDeliveryBoys: () => api.get('/delivery-boy/admin/delivery-boys'),
  getAdminStats: () => api.get('/delivery-boy/admin/stats'),
  
  // Admin bottle management endpoints
  getPendingBottleCharges: () => api.get('/delivery-boy/admin/bottles/charges/pending'),
  updateBottleChargeStatus: (uuid, status, waivedReason = '') => 
    api.put(`/delivery-boy/admin/bottles/charges/${uuid}/status`, { status, waivedReason }),
  getBottleStats: () => api.get('/delivery-boy/admin/bottles/stats'),
  
  // Customer bottle tracking endpoints (for admin)
  getCustomerBottleBalances: (params = {}) => api.get('/bottles/balances', { params }),
  getCustomerBottleDetail: (customerUuid) => api.get(`/bottles/customer/${customerUuid}/balance`),
  getCustomerBottleTransactions: (customerUuid, params = {}) => 
    api.get(`/bottles/customer/${customerUuid}/transactions`, { params }),
  adjustCustomerBottleBalance: (data) => api.post('/bottles/adjust-balance', data),
};

// Delivery Management API (Order groups and grouping)
export const deliveryManagementAPI = {
  // Auto-create order groups by proximity
  autoCreateGroups: (deliveryDate, radiusKm = 1) =>
    api.post('/delivery-management/auto-create-groups', { deliveryDate, radiusKm }),
  
  // Get all groups for a date
  getGroupsByDate: (date) => api.get('/delivery-management/groups', { params: { date } }),
  
  // Get single group details
  getGroupDetails: (groupUuid) => api.get(`/delivery-management/groups/${groupUuid}`),
  
  // Assign group to delivery boy
  assignGroup: (groupUuid, deliveryBoyUuid) =>
    api.post('/delivery-management/assign-group', { groupUuid, deliveryBoyUuid }),
  
  // Unassign group
  unassignGroup: (groupUuid) =>
    api.post('/delivery-management/unassign-group', { groupUuid }),
  
  // Delete/ungroup
  deleteGroup: (groupUuid) => api.delete(`/delivery-management/groups/${groupUuid}`),
};

// Scheduler API
export const schedulerAPI = {
  getLogs: (params = {}) => api.get('/scheduler/logs', { params }),
  getStatus: () => api.get('/scheduler/status'),
  runNow: () => api.post('/scheduler/run-now'),
  getHealth: () => api.get('/scheduler/health'),
};

// Special Offers API
export const specialOffersAPI = {
  getAll: (params) => api.get('/special-offers/admin/all', { params }),
  getPublic: () => api.get('/special-offers'),
  getById: (uuid) => api.get(`/special-offers/${uuid}`),
  create: (data) => {
    if (data instanceof FormData) {
      return api.post('/special-offers', data);
    }
    return api.post('/special-offers', data);
  },
  update: (uuid, data) => {
    if (data instanceof FormData) {
      return api.put(`/special-offers/${uuid}`, data);
    }
    return api.put(`/special-offers/${uuid}`, data);
  },
  delete: (uuid) => api.delete(`/special-offers/${uuid}`),
  reorder: (offers) => api.post('/special-offers/admin/reorder', { offers }),
};

// Future Products API
export const futureProductsAPI = {
  getAll: (params) => api.get('/future-products/admin/all', { params }),
  getPublic: () => api.get('/future-products'),
  getById: (uuid) => api.get(`/future-products/${uuid}`),
  create: (data) => {
    if (data instanceof FormData) {
      return api.post('/future-products', data);
    }
    return api.post('/future-products', data);
  },
  update: (uuid, data) => {
    if (data instanceof FormData) {
      return api.put(`/future-products/${uuid}`, data);
    }
    return api.put(`/future-products/${uuid}`, data);
  },
  delete: (uuid) => api.delete(`/future-products/${uuid}`),
  reorder: (products) => api.post('/future-products/admin/reorder', { products }),
};

export default api; 